interface PullToRefreshIndicatorProps {
  /** Deslocamento atual do conteúdo (px). */
  pullDistance: number;
  /** True enquanto a atualização está em andamento. */
  isRefreshing: boolean;
  /** Distância de acionamento (px). */
  threshold: number;
  /** Altura da TopBar fixa, para posicionar o indicador logo abaixo dela. */
  topBarHeight: number;
  /** Cor de fundo da faixa, alinhada à primeira seção do feed. */
  backgroundColor?: string;
}

/**
 * Indicador visual do pull-to-refresh. Ocupa o espaço revelado quando o
 * feed é arrastado para baixo (entre a TopBar fixa e o conteúdo do feed).
 *
 * - Enquanto puxa: o anel gira proporcionalmente ao progresso até o limiar.
 * - Ao atingir o limiar: o anel completa a volta (feedback de "pode soltar").
 * - Durante o refresh: o anel gira em loop.
 */
export function PullToRefreshIndicator({
  pullDistance,
  isRefreshing,
  threshold,
  topBarHeight,
  backgroundColor = "#F3EBFF",
}: PullToRefreshIndicatorProps) {
  if (pullDistance <= 0 && !isRefreshing) return null;

  const progress = Math.min(1, pullDistance / threshold);
  const opacity = Math.min(1, pullDistance / (threshold * 0.5));
  // Gira até 270° conforme o progresso; ao acionar, completa para 360°.
  const rotation = isRefreshing ? 0 : progress * 270;

  return (
    <div
      style={{
        position: "absolute",
        top: topBarHeight,
        left: 0,
        right: 0,
        height: Math.max(pullDistance, 0),
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingBottom: 10,
        overflow: "hidden",
        pointerEvents: "none",
        backgroundColor,
        zIndex: 5,
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          opacity,
          transform: isRefreshing ? undefined : `scale(${0.6 + progress * 0.4})`,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "2.5px solid #E2E8F0",
            borderTopColor: "#2563eb",
            boxSizing: "border-box",
            transform: isRefreshing ? undefined : `rotate(${rotation}deg)`,
            transition: isRefreshing ? undefined : "transform 0.08s linear",
            animation: isRefreshing
              ? "ptr-spin 0.8s linear infinite"
              : undefined,
          }}
        />
      </div>
      <style>{`
        @keyframes ptr-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
